import { z } from 'zod'

const RawEnvSchema = z.object({
  VITE_API_HOST: z.string().url(),
  VITE_PROD_API_HOSTS: z.string().default(''),
  VITE_ENV: z.enum(['local', 'dev', 'prod']).default('local'),
  VITE_USE_LOCAL_PROXY: z
    .union([z.literal('true'), z.literal('false'), z.literal('')])
    .default('false'),
})

// safeParse rather than parse so a misconfigured build can't throw at
// module-evaluation time — that throw happens BEFORE `start()` runs in
// main.ts, leaving the boot splash up forever with no feedback. On
// failure we expose `envError` (which the bootstrap surfaces as a
// fatal error screen) and fall back to a safe, prod-flagged shape so
// importing `env` never crashes and no dev surfaces leak.
const parsed = RawEnvSchema.safeParse(import.meta.env)

export const envError: string | null = parsed.success
  ? null
  : `Invalid environment configuration: ${parsed.error.issues
      .map((i) => `${i.path.join('.') || '(root)'} — ${i.message}`)
      .join('; ')}`

const raw = parsed.success
  ? parsed.data
  : ({
      VITE_API_HOST: '',
      VITE_PROD_API_HOSTS: '',
      VITE_ENV: 'prod',
      VITE_USE_LOCAL_PROXY: 'false',
    } as const)

const prodHosts = raw.VITE_PROD_API_HOSTS.split(',')
  .map((s) => s.trim())
  .filter(Boolean)

const useLocalProxy = raw.VITE_USE_LOCAL_PROXY === 'true'

export const env = {
  /**
   * The host to which API requests are addressed. Empty string means
   * "current origin", which is what we want when the Vite dev server is
   * proxying `/wp-json/*` (see vite.config.ts).
   */
  apiHost: useLocalProxy ? '' : raw.VITE_API_HOST,
  /** The configured upstream — useful for displaying status etc. */
  upstreamApiHost: raw.VITE_API_HOST,
  prodApiHosts: prodHosts,
  envName: raw.VITE_ENV,
  isLocal: raw.VITE_ENV === 'local',
  isDev: raw.VITE_ENV === 'dev',
  isProd: raw.VITE_ENV === 'prod',
  useLocalProxy,
} as const

export type AppEnv = typeof env
